/**
 * ProImageUploader - Production-Ready Multi-Image Upload Component
 * 
 * Features:
 * - Multi-image upload (10-20 images)
 * - Auto compression (browser-image-compression)
 * - Drag & drop with reordering
 * - Preview, rename, remove images
 * - Retry mechanism (3 retries)
 * - Progress tracking
 * - Watermark support
 * - JPEG, PNG, WEBP support (max 20MB)
 */

import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import imageCompression from 'browser-image-compression';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  rectSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button } from './button';
import { Input } from './input';
import { Progress } from './progress';
import { 
  Upload, X, GripVertical, Image as ImageIcon, AlertCircle, 
  CheckCircle, Loader2, ZoomIn, Edit2, RotateCcw, Trash2,
  ImagePlus, FileWarning, RefreshCw
} from 'lucide-react';

// Constants
const MAX_FILE_SIZE = 20 * 1024 * 1024; // 20MB
const COMPRESSION_THRESHOLD = 1 * 1024 * 1024; // 1MB
const TARGET_MAX_SIZE_MB = 0.8; // 800KB
const MAX_WIDTH_HEIGHT = 1920;
const MAX_FILES = 20;
const MAX_RETRIES = 3;
const ACCEPTED_TYPES = {
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/png': ['.png'],
  'image/webp': ['.webp'],
};

// Compression options
const compressionOptions = {
  maxSizeMB: TARGET_MAX_SIZE_MB,
  maxWidthOrHeight: MAX_WIDTH_HEIGHT,
  useWebWorker: true,
  fileType: 'image/webp',
  initialQuality: 0.8,
};

// Status types
const STATUS = {
  PENDING: 'pending',
  COMPRESSING: 'compressing',
  UPLOADING: 'uploading',
  SUCCESS: 'success',
  ERROR: 'error',
  RETRYING: 'retrying',
};

// Sortable Image Item Component
const SortableImageItem = ({ 
  image, 
  onRemove, 
  onRename, 
  onPreview, 
  onRetry,
  isUploading 
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [newName, setNewName] = useState(image.name);
  
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: image.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : 1,
  };

  const handleSaveName = () => {
    onRename(image.id, newName);
    setIsEditing(false);
  };

  const getStatusIcon = () => {
    switch (image.status) {
      case STATUS.COMPRESSING:
        return <Loader2 className="w-4 h-4 animate-spin text-blue-500" />;
      case STATUS.UPLOADING:
        return <Loader2 className="w-4 h-4 animate-spin text-sky-500" />;
      case STATUS.SUCCESS:
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case STATUS.ERROR:
        return <AlertCircle className="w-4 h-4 text-red-500" />;
      case STATUS.RETRYING:
        return <RefreshCw className="w-4 h-4 animate-spin text-orange-500" />;
      default:
        return null;
    }
  };

  const getStatusText = () => {
    switch (image.status) {
      case STATUS.COMPRESSING:
        return 'Compressing...';
      case STATUS.UPLOADING:
        return `Uploading ${image.progress || 0}%`;
      case STATUS.SUCCESS:
        return 'Uploaded';
      case STATUS.ERROR:
        return image.error || 'Failed';
      case STATUS.RETRYING:
        return `Retrying (${image.retryCount}/${MAX_RETRIES})...`;
      default:
        return 'Ready';
    }
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`relative bg-white rounded-lg border-2 overflow-hidden group transition-all
        ${isDragging ? 'border-sky-500 shadow-lg' : 'border-slate-200 hover:border-slate-300'}
        ${image.status === STATUS.ERROR ? 'border-red-300' : ''}
        ${image.status === STATUS.SUCCESS ? 'border-green-300' : ''}`}
    >
      {/* Drag Handle */}
      <div
        {...attributes}
        {...listeners}
        className="absolute top-2 left-2 z-10 p-1 bg-white/80 rounded cursor-grab active:cursor-grabbing opacity-0 group-hover:opacity-100 transition-opacity"
      >
        <GripVertical className="w-4 h-4 text-slate-500" />
      </div>

      {/* Image Preview */}
      <div 
        className="aspect-square overflow-hidden cursor-pointer"
        onClick={() => onPreview(image)}
      >
        <img
          src={image.preview}
          alt={image.name}
          className="w-full h-full object-cover transition-transform hover:scale-105"
        />
        
        {/* Overlay for zoom */}
        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center pointer-events-none">
          <ZoomIn className="w-8 h-8 text-white" />
        </div>
      </div>

      {/* Progress Bar */}
      {(image.status === STATUS.UPLOADING || image.status === STATUS.COMPRESSING) && (
        <div className="absolute bottom-0 left-0 right-0">
          <Progress value={image.progress || 0} className="h-1 rounded-none" />
        </div>
      )}

      {/* Status Badge */}
      <div className="absolute top-2 right-2 z-10">
        <div className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium
          ${image.status === STATUS.SUCCESS ? 'bg-green-100 text-green-700' : ''}
          ${image.status === STATUS.ERROR ? 'bg-red-100 text-red-700' : ''}
          ${image.status === STATUS.COMPRESSING ? 'bg-blue-100 text-blue-700' : ''}
          ${image.status === STATUS.UPLOADING ? 'bg-sky-100 text-sky-700' : ''}
          ${image.status === STATUS.RETRYING ? 'bg-orange-100 text-orange-700' : ''}
          ${image.status === STATUS.PENDING ? 'bg-slate-100 text-slate-700' : ''}`}
        >
          {getStatusIcon()}
          <span className="hidden sm:inline">{getStatusText()}</span>
        </div>
      </div>

      {/* Image Info */}
      <div className="p-3 bg-white">
        {isEditing ? (
          <div className="flex gap-2">
            <Input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              className="h-8 text-sm"
              onKeyDown={(e) => e.key === 'Enter' && handleSaveName()}
              autoFocus
            />
            <Button size="sm" onClick={handleSaveName} className="h-8 px-2">
              <CheckCircle className="w-4 h-4" />
            </Button>
          </div>
        ) : (
          <div className="flex items-center justify-between">
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-slate-900 truncate">{image.name}</p>
              <p className="text-xs text-slate-500">
                {image.compressedSize 
                  ? `${(image.compressedSize / 1024).toFixed(0)}KB (was ${(image.originalSize / 1024).toFixed(0)}KB)`
                  : `${(image.originalSize / 1024).toFixed(0)}KB`}
              </p>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex gap-1 mt-2">
          {!isEditing && (
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => setIsEditing(true)}
              className="h-7 px-2"
              disabled={isUploading}
            >
              <Edit2 className="w-3 h-3" />
            </Button>
          )}
          {image.status === STATUS.ERROR && (
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => onRetry(image.id)}
              className="h-7 px-2 text-orange-600 hover:text-orange-700"
            >
              <RotateCcw className="w-3 h-3" />
            </Button>
          )}
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => onRemove(image.id)}
            className="h-7 px-2 text-red-600 hover:text-red-700 ml-auto"
            disabled={isUploading && image.status !== STATUS.ERROR}
          >
            <Trash2 className="w-3 h-3" />
          </Button>
        </div>
      </div>
    </div>
  );
};

// Main Component
export const ProImageUploader = ({
  onUploadComplete,
  maxFiles = MAX_FILES,
  watermarkEnabled = true,
  watermarkText = 'Raj Aluminium',
  watermarkContact = '085128 53955',
  watermarkPosition = 'bottom-right',
  watermarkOpacity = 40,
  apiEndpoint = '/api/admin/upload',
}) => {
  const [images, setImages] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const [globalStatus, setGlobalStatus] = useState('');
  const uploadQueue = useRef([]);
  const abortControllers = useRef({});

  // Auth token - matches AdminContext storage key
  const getAuthToken = () => localStorage.getItem('admin_token');

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Cleanup previews on unmount
  useEffect(() => {
    return () => {
      images.forEach(img => {
        if (img.preview) URL.revokeObjectURL(img.preview);
      });
    };
  }, []);

  // Generate unique ID
  const generateId = () => `img-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

  // Compress image
  const compressImage = async (file, imageId) => {
    try {
      updateImageStatus(imageId, STATUS.COMPRESSING, { progress: 0 });
      
      const needsCompression = file.size > COMPRESSION_THRESHOLD;
      
      if (!needsCompression) {
        updateImageStatus(imageId, STATUS.PENDING, { 
          compressedSize: file.size,
          compressedFile: file 
        });
        return file;
      }

      setGlobalStatus(`Compressing ${file.name}...`);
      
      const compressedFile = await imageCompression(file, {
        ...compressionOptions,
        onProgress: (progress) => {
          updateImageStatus(imageId, STATUS.COMPRESSING, { progress: Math.round(progress) });
        },
      });

      updateImageStatus(imageId, STATUS.PENDING, { 
        compressedSize: compressedFile.size,
        compressedFile 
      });

      console.log(`Compressed ${file.name}: ${(file.size/1024).toFixed(0)}KB → ${(compressedFile.size/1024).toFixed(0)}KB`);
      return compressedFile;
    } catch (error) {
      console.error('Compression error:', error);
      // Return original file if compression fails
      updateImageStatus(imageId, STATUS.PENDING, { 
        compressedFile: file,
        compressedSize: file.size 
      });
      return file;
    }
  };

  // Update image status
  const updateImageStatus = (imageId, status, updates = {}) => {
    setImages(prev => prev.map(img => 
      img.id === imageId ? { ...img, status, ...updates } : img
    ));
  };

  // Upload single image with retry
  const uploadImage = async (image, retryCount = 0) => {
    const token = getAuthToken();
    if (!token) {
      updateImageStatus(image.id, STATUS.ERROR, { error: 'Not authenticated' });
      return null;
    }

    const fileToUpload = image.compressedFile || image.file;
    const formData = new FormData();
    
    // Ensure filename has correct extension based on file type
    let filename = image.name;
    if (fileToUpload.type === 'image/webp' && !filename.toLowerCase().endsWith('.webp')) {
      filename = filename.replace(/\.[^/.]+$/, '') + '.webp';
    } else if (fileToUpload.type === 'image/jpeg' && !filename.toLowerCase().match(/\.(jpg|jpeg)$/)) {
      filename = filename.replace(/\.[^/.]+$/, '') + '.jpg';
    } else if (fileToUpload.type === 'image/png' && !filename.toLowerCase().endsWith('.png')) {
      filename = filename.replace(/\.[^/.]+$/, '') + '.png';
    } else if (!filename.match(/\.(jpg|jpeg|png|webp)$/i)) {
      // Default to webp if no extension (compressed files are webp)
      filename = filename + '.webp';
    }
    
    formData.append('file', fileToUpload, filename);
    
    // Watermark settings
    if (watermarkEnabled) {
      formData.append('apply_watermark', 'true');
      formData.append('watermark_text', watermarkText);
      formData.append('watermark_contact', watermarkContact);
      formData.append('watermark_position', watermarkPosition);
      formData.append('watermark_opacity', watermarkOpacity.toString());
    }

    // Create abort controller for this upload
    const controller = new AbortController();
    abortControllers.current[image.id] = controller;

    try {
      updateImageStatus(image.id, STATUS.UPLOADING, { progress: 0, retryCount });

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}${apiEndpoint}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
        signal: controller.signal,
      });

      if (!response.ok) {
        throw new Error(`Upload failed: ${response.status}`);
      }

      const result = await response.json();
      
      updateImageStatus(image.id, STATUS.SUCCESS, { 
        progress: 100,
        uploadedUrl: result.url,
        filename: result.filename 
      });

      delete abortControllers.current[image.id];
      return result;

    } catch (error) {
      if (error.name === 'AbortError') {
        updateImageStatus(image.id, STATUS.ERROR, { error: 'Upload cancelled' });
        return null;
      }

      // Retry logic
      if (retryCount < MAX_RETRIES) {
        console.log(`Retry ${retryCount + 1}/${MAX_RETRIES} for ${image.name}`);
        updateImageStatus(image.id, STATUS.RETRYING, { retryCount: retryCount + 1 });
        
        // Wait before retry (exponential backoff)
        await new Promise(resolve => setTimeout(resolve, 1000 * (retryCount + 1)));
        
        return uploadImage(image, retryCount + 1);
      }

      updateImageStatus(image.id, STATUS.ERROR, { 
        error: error.message || 'Upload failed',
        retryCount 
      });
      delete abortControllers.current[image.id];
      return null;
    }
  };

  // Process dropped files
  const onDrop = useCallback(async (acceptedFiles, rejectedFiles) => {
    // Handle rejected files
    if (rejectedFiles.length > 0) {
      rejectedFiles.forEach(({ file, errors }) => {
        console.warn(`Rejected ${file.name}:`, errors.map(e => e.message).join(', '));
      });
    }

    // Check max files limit
    const currentCount = images.length;
    const availableSlots = maxFiles - currentCount;
    
    if (availableSlots <= 0) {
      setGlobalStatus(`Maximum ${maxFiles} images allowed`);
      return;
    }

    const filesToAdd = acceptedFiles.slice(0, availableSlots);
    
    // Create image objects with previews
    const newImages = filesToAdd.map(file => ({
      id: generateId(),
      file,
      name: file.name.replace(/\.[^/.]+$/, ''), // Remove extension for display
      originalName: file.name,
      originalSize: file.size,
      preview: URL.createObjectURL(file),
      status: STATUS.PENDING,
      progress: 0,
      retryCount: 0,
    }));

    setImages(prev => [...prev, ...newImages]);

    // Compress images in parallel (limited concurrency)
    setGlobalStatus('Preparing images...');
    const compressionPromises = newImages.map(img => compressImage(img.file, img.id));
    await Promise.all(compressionPromises);
    setGlobalStatus('');

  }, [images.length, maxFiles]);

  // Dropzone configuration
  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop,
    accept: ACCEPTED_TYPES,
    maxSize: MAX_FILE_SIZE,
    multiple: true,
    noClick: images.length > 0,
    noKeyboard: false,
  });

  // Handle drag end for reordering
  const handleDragEnd = (event) => {
    const { active, over } = event;
    
    if (active.id !== over?.id) {
      setImages((items) => {
        const oldIndex = items.findIndex(i => i.id === active.id);
        const newIndex = items.findIndex(i => i.id === over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  // Remove image
  const handleRemove = (imageId) => {
    // Abort upload if in progress
    if (abortControllers.current[imageId]) {
      abortControllers.current[imageId].abort();
    }
    
    setImages(prev => {
      const img = prev.find(i => i.id === imageId);
      if (img?.preview) URL.revokeObjectURL(img.preview);
      return prev.filter(i => i.id !== imageId);
    });
  };

  // Rename image
  const handleRename = (imageId, newName) => {
    setImages(prev => prev.map(img => 
      img.id === imageId ? { ...img, name: newName } : img
    ));
  };

  // Retry failed upload
  const handleRetry = async (imageId) => {
    const image = images.find(i => i.id === imageId);
    if (image) {
      await uploadImage(image);
    }
  };

  // Upload all images
  const handleUploadAll = async () => {
    const pendingImages = images.filter(img => 
      img.status === STATUS.PENDING || img.status === STATUS.ERROR
    );

    if (pendingImages.length === 0) {
      setGlobalStatus('No images to upload');
      return;
    }

    setIsUploading(true);
    setGlobalStatus(`Uploading ${pendingImages.length} images...`);

    // Upload images (with limited concurrency of 3)
    const concurrencyLimit = 3;
    const results = [];
    
    for (let i = 0; i < pendingImages.length; i += concurrencyLimit) {
      const batch = pendingImages.slice(i, i + concurrencyLimit);
      const batchResults = await Promise.all(batch.map(img => uploadImage(img)));
      results.push(...batchResults);
    }

    setIsUploading(false);
    
    const successCount = results.filter(r => r !== null).length;
    const failCount = results.filter(r => r === null).length;
    
    if (failCount > 0) {
      setGlobalStatus(`Uploaded ${successCount} images. ${failCount} failed.`);
    } else {
      setGlobalStatus(`Successfully uploaded ${successCount} images!`);
    }

    // Callback with uploaded URLs
    if (onUploadComplete) {
      const uploadedUrls = images
        .filter(img => img.status === STATUS.SUCCESS && img.uploadedUrl)
        .map(img => img.uploadedUrl);
      onUploadComplete(uploadedUrls);
    }

    // Clear status after delay
    setTimeout(() => setGlobalStatus(''), 3000);
  };

  // Remove all images
  const handleClearAll = () => {
    // Abort all uploads
    Object.values(abortControllers.current).forEach(controller => controller.abort());
    abortControllers.current = {};
    
    images.forEach(img => {
      if (img.preview) URL.revokeObjectURL(img.preview);
    });
    setImages([]);
  };

  // Get summary stats
  const getStats = () => {
    const pending = images.filter(i => i.status === STATUS.PENDING).length;
    const uploading = images.filter(i => i.status === STATUS.UPLOADING || i.status === STATUS.COMPRESSING).length;
    const success = images.filter(i => i.status === STATUS.SUCCESS).length;
    const failed = images.filter(i => i.status === STATUS.ERROR).length;
    return { pending, uploading, success, failed, total: images.length };
  };

  const stats = getStats();

  return (
    <div className="w-full space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-slate-900">Upload Images</h3>
          <p className="text-sm text-slate-500">
            Drag & drop or click to upload. Max {maxFiles} images, 20MB each.
          </p>
        </div>
        {images.length > 0 && (
          <div className="flex items-center gap-2 text-sm">
            <span className="text-slate-600">{stats.total} images</span>
            {stats.success > 0 && (
              <span className="text-green-600">({stats.success} uploaded)</span>
            )}
            {stats.failed > 0 && (
              <span className="text-red-600">({stats.failed} failed)</span>
            )}
          </div>
        )}
      </div>

      {/* Dropzone */}
      <div
        {...getRootProps()}
        className={`relative border-2 border-dashed rounded-xl transition-all cursor-pointer
          ${isDragActive 
            ? 'border-sky-500 bg-sky-50' 
            : 'border-slate-300 hover:border-slate-400 bg-slate-50'
          }
          ${images.length === 0 ? 'p-12' : 'p-4'}`}
      >
        <input {...getInputProps()} />
        
        {images.length === 0 ? (
          <div className="text-center">
            <div className="w-16 h-16 mx-auto bg-slate-100 rounded-full flex items-center justify-center mb-4">
              <ImagePlus className="w-8 h-8 text-slate-400" />
            </div>
            <p className="text-lg font-medium text-slate-700">
              {isDragActive ? 'Drop images here' : 'Drag & drop images here'}
            </p>
            <p className="text-sm text-slate-500 mt-1">
              or click to browse
            </p>
            <p className="text-xs text-slate-400 mt-2">
              JPEG, PNG, WEBP • Max 20MB • Auto-compressed to ~800KB
            </p>
          </div>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={images.map(i => i.id)}
              strategy={rectSortingStrategy}
            >
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                {images.map((image) => (
                  <SortableImageItem
                    key={image.id}
                    image={image}
                    onRemove={handleRemove}
                    onRename={handleRename}
                    onPreview={setPreviewImage}
                    onRetry={handleRetry}
                    isUploading={isUploading}
                  />
                ))}
                
                {/* Add More Button */}
                {images.length < maxFiles && (
                  <button
                    type="button"
                    onClick={open}
                    className="aspect-square border-2 border-dashed border-slate-300 rounded-lg flex flex-col items-center justify-center gap-2 text-slate-500 hover:border-sky-500 hover:text-sky-600 hover:bg-sky-50 transition-all"
                  >
                    <ImagePlus className="w-8 h-8" />
                    <span className="text-sm font-medium">Add More</span>
                  </button>
                )}
              </div>
            </SortableContext>
          </DndContext>
        )}
      </div>

      {/* Status Message */}
      {globalStatus && (
        <div className={`p-3 rounded-lg text-sm flex items-center gap-2
          ${globalStatus.includes('failed') ? 'bg-red-50 text-red-700' : ''}
          ${globalStatus.includes('Successfully') ? 'bg-green-50 text-green-700' : ''}
          ${!globalStatus.includes('failed') && !globalStatus.includes('Successfully') ? 'bg-blue-50 text-blue-700' : ''}`}
        >
          {globalStatus.includes('failed') && <AlertCircle className="w-4 h-4" />}
          {globalStatus.includes('Successfully') && <CheckCircle className="w-4 h-4" />}
          {!globalStatus.includes('failed') && !globalStatus.includes('Successfully') && (
            <Loader2 className="w-4 h-4 animate-spin" />
          )}
          {globalStatus}
        </div>
      )}

      {/* Action Buttons */}
      {images.length > 0 && (
        <div className="flex flex-wrap gap-3">
          <Button
            onClick={handleUploadAll}
            disabled={isUploading || stats.pending === 0 && stats.failed === 0}
            className="bg-sky-500 hover:bg-sky-600"
          >
            {isUploading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Uploading...
              </>
            ) : (
              <>
                <Upload className="w-4 h-4 mr-2" />
                Upload {stats.pending + stats.failed > 0 ? `(${stats.pending + stats.failed})` : 'All'}
              </>
            )}
          </Button>
          
          <Button
            variant="outline"
            onClick={handleClearAll}
            disabled={isUploading}
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Clear All
          </Button>

          <Button
            variant="outline"
            onClick={open}
            disabled={isUploading || images.length >= maxFiles}
          >
            <ImagePlus className="w-4 h-4 mr-2" />
            Add More
          </Button>
        </div>
      )}

      {/* Watermark Settings Info */}
      {watermarkEnabled && images.length > 0 && (
        <div className="p-3 bg-slate-50 rounded-lg text-sm text-slate-600">
          <span className="font-medium">Watermark:</span> {watermarkText} | {watermarkContact} 
          <span className="text-slate-400 ml-2">({watermarkPosition}, {watermarkOpacity}% opacity)</span>
        </div>
      )}

      {/* Image Preview Modal */}
      {previewImage && (
        <div 
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
          onClick={() => setPreviewImage(null)}
        >
          <button
            className="absolute top-4 right-4 p-2 bg-white/10 hover:bg-white/20 rounded-full transition-colors"
            onClick={() => setPreviewImage(null)}
          >
            <X className="w-6 h-6 text-white" />
          </button>
          <img
            src={previewImage.preview}
            alt={previewImage.name}
            className="max-w-full max-h-full object-contain"
            onClick={(e) => e.stopPropagation()}
          />
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
            <span className="text-white text-sm">
              {previewImage.name} • {(previewImage.compressedSize || previewImage.originalSize) / 1024 | 0}KB
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProImageUploader;
